import { useMemo, useState } from "react";
import {
  Drawer,
  TextInput,
  Stack,
  Text,
  Badge,
  UnstyledButton,
  Group,
  ScrollArea,
  Alert,
} from "@mantine/core";
import {
  IconSearch,
  IconInfoCircle,
  IconClipboardText,
} from "@tabler/icons-react";

/**
 * One searchable, insertable piece of resident/facility context — built from
 * data the app already fetches elsewhere (physicians, pharmacies, insurance,
 * emergency contacts, care plans, allergies, medications, the resident's own
 * facility). Never a new backend endpoint; this is a read-only re-surfacing
 * of existing hook data as one flat searchable list.
 */
export interface LookupDrawerItem {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  /** The exact text inserted into the active field when clicked (text/textarea targets). */
  copyText: string;
  /**
   * Code-review fix ("couldn't insert weight here"): a bare numeric value
   * (e.g. 165 for a weight reading), present ONLY for single-measurement
   * items that are genuinely one number — never for combined/multi-part
   * values (a blood pressure reading is two numbers, an assistance-level
   * ADL column is an enum string). A number-type target field can only ever
   * insert via this, never copyText (a NumberInput cannot hold a sentence);
   * an item with no numericValue simply can't be inserted into a number
   * field at all.
   */
  numericValue?: number;
}

interface LookupDrawerProps {
  opened: boolean;
  onClose: () => void;
  items: LookupDrawerItem[];
  /** Label of the field currently focused in the form, or null if none. */
  activeFieldLabel: string | null;
  /** True when the focused field is a number-type field — only items with numericValue can insert into it. */
  activeFieldIsNumber?: boolean;
  onInsert: (item: LookupDrawerItem) => void;
}

/**
 * A side panel staff can open while filling out a resident form to search
 * resident/facility info they'd otherwise have to remember or look up
 * elsewhere, then insert it straight into whichever text field they last
 * clicked into — no manual copy/paste. Distinct from the *_lookup field
 * types (which resolve a SPECIFIC field to one real record); this is a
 * general reference tool for free-text fields with no dedicated lookup type
 * (e.g. "Recent Falls", "Dietary Restrictions").
 */
export function LookupDrawer({
  opened,
  onClose,
  items,
  activeFieldLabel,
  activeFieldIsNumber,
  onInsert,
}: LookupDrawerProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.subtitle?.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        // Code-review fix: a combined item (e.g. "Latest Vitals" bundling BP/
        // HR/temp/weight/etc. into one row) has a title/category too generic
        // to match a specific measurement name — "weight" wouldn't match
        // "Latest Vitals"/"Vitals" even though the reading is right there in
        // copyText. Searching the actual insertable text too means any term
        // that appears in what gets inserted is findable, not just the
        // display chrome around it.
        item.copyText.toLowerCase().includes(q),
    );
  }, [items, query]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Look up resident & facility info"
      position="right"
      size="sm"
      // Above the form sheet's own light-mode scope but below nothing else —
      // this drawer is opened from inside a form-fill Modal, which already
      // has its own stacking context.
      zIndex={1000}
    >
      <Stack gap="sm">
        {activeFieldLabel ? (
          <Alert
            icon={<IconClipboardText size={14} />}
            color="blue"
            variant="light"
            p="xs"
          >
            <Text size="xs">
              Inserting into: <strong>{activeFieldLabel}</strong>
            </Text>
          </Alert>
        ) : (
          <Alert
            icon={<IconInfoCircle size={14} />}
            color="gray"
            variant="light"
            p="xs"
          >
            <Text size="xs">
              Click into a text field on the form first, then click a result
              here to insert it.
            </Text>
          </Alert>
        )}

        <TextInput
          placeholder="Search physicians, pharmacies, insurance, allergies…"
          leftSection={<IconSearch size={14} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          autoFocus
        />

        <ScrollArea h="calc(100vh - 220px)">
          <Stack gap={6}>
            {filtered.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                {items.length === 0
                  ? "No resident or facility info on file yet."
                  : "No matches."}
              </Text>
            ) : (
              filtered.map((item) => {
                // A number-type target can only ever accept a bare numeric
                // value — an item with no numericValue (a physician, an
                // enum-valued ADL column, a two-part BP reading) simply
                // cannot go there.
                const insertable =
                  !!activeFieldLabel &&
                  (!activeFieldIsNumber || item.numericValue !== undefined);
                return (
                  <UnstyledButton
                    key={item.id}
                    onClick={() => insertable && onInsert(item)}
                    disabled={!insertable}
                    p="xs"
                    style={{
                      borderRadius: 6,
                      border: "1px solid var(--mantine-color-gray-3)",
                      opacity: insertable ? 1 : 0.5,
                    }}
                  >
                    <Group gap="xs" mb={2}>
                      <Badge size="xs" variant="light">
                        {item.category}
                      </Badge>
                      {activeFieldIsNumber &&
                        item.numericValue === undefined && (
                          <Text size="xs" c="dimmed">
                            Not a single number — can't insert here
                          </Text>
                        )}
                    </Group>
                    <Text size="sm" fw={600}>
                      {item.title}
                    </Text>
                    {item.subtitle && (
                      <Text size="xs" c="dimmed">
                        {item.subtitle}
                      </Text>
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </Stack>
        </ScrollArea>
      </Stack>
    </Drawer>
  );
}
